/**
 * Modal simple para marcar una ruta pendiente de revisión NS como revisada.
 * A diferencia de SubestadoModal (ControlSemanal), este modal NO cambia estados/subestados
 * de guías ni dispara ninguna acción correctiva — solo registra que Operaciones revisó
 * el caso, con observaciones de texto libre opcionales.
 */

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Spinner, Alert } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';
import { useNsReconciliacion } from '../../../../Mantenedores/hooks/useNsReconciliacion';

const MarcarRevisadoModal = ({ show, onHide, ruta, onExecute, loading }) => {
  const { handleGetGuiasDeRutaPendiente } = useNsReconciliacion();
  const [observaciones, setObservaciones] = useState('');

  // Detalle ACTUAL de guías (código + subestado vigente) de la ruta — se consulta en
  // vivo cada vez que se abre el modal, en vez de mostrar solo el conteo
  // (`guias_no_definitivas`) capturado en el momento en que se detectó la ruta pendiente.
  const [guias, setGuias] = useState([]);
  const [guiasLoading, setGuiasLoading] = useState(false);
  const [guiasError, setGuiasError] = useState(null);

  // Limpiar observaciones y recargar el detalle de guías cada vez que se abre el modal
  // para una ruta nueva
  useEffect(() => {
    if (show && ruta) {
      setObservaciones('');
      setGuias([]);
      setGuiasError(null);
      setGuiasLoading(true);

      handleGetGuiasDeRutaPendiente(ruta.id)
        .then((response) => {
          if (response.success) {
            setGuias(response.data?.guias || []);
          } else {
            setGuiasError(response.error);
          }
        })
        .finally(() => setGuiasLoading(false));
    }
    // No se incluye handleGetGuiasDeRutaPendiente en las dependencias a propósito: el hook
    // la recrea en cada render (no usa useCallback), incluirla dispararía este efecto en
    // un loop infinito. Solo debe re-ejecutarse cuando cambia `show` o `ruta`.
  }, [show, ruta]);

  if (!show) return null;

  const handleConfirm = () => {
    onExecute(observaciones.trim() || undefined);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaIcons.FaCheckCircle className="me-2" />
          Marcar ruta como revisada
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {ruta && (
          <div className="alert alert-info">
            <strong>Ruta:</strong> {ruta.ruta}
            <br />
            <strong>Fecha:</strong> {ruta.fecha}
            <br />
            <strong>Guías no-definitivas (al momento de detectarse):</strong> {ruta.guias_no_definitivas}
          </div>
        )}

        <h6 className="fw-bold">Guías actualmente no-definitivas de esta ruta</h6>
        {guiasLoading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Cargando guías...
          </div>
        ) : guiasError ? (
          <Alert variant="warning" className="mb-3">
            No se pudo cargar el detalle de guías: {guiasError}. Puede igual marcar la ruta
            como revisada si ya cuenta con la información necesaria.
          </Alert>
        ) : guias.length === 0 ? (
          <Alert variant="secondary" className="mb-3">
            No se encontraron guías no-definitivas para esta ruta en este momento — es posible
            que ya se hayan resuelto desde que se detectó el caso.
          </Alert>
        ) : (
          <div className="table-responsive mb-3" style={{ maxHeight: 260, overflowY: 'auto' }}>
            <Table striped bordered hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Guía</th>
                  <th>Subestado vigente</th>
                </tr>
              </thead>
              <tbody>
                {guias.map((g) => (
                  <tr key={g.guia_id}>
                    <td>{g.guia}</td>
                    <td>{g.sub_estado}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        <Form.Group className="mb-2">
          <Form.Label className="fw-bold">Observaciones (opcional)</Form.Label>
          <Form.Control
            as="textarea"
            rows={4}
            maxLength={1000}
            placeholder="Ej: ruta cancelada en DispatchTrack, se confirmó con el conductor..."
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            disabled={loading}
          />
        </Form.Group>
        <small className="text-muted">
          Esta acción solo deja registro de que la ruta fue revisada manualmente. No modifica
          guías ni rutas — si corresponde una corrección, debe hacerse desde Control Semanal.
        </small>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="success" onClick={handleConfirm} disabled={loading}>
          {loading ? (
            <>
              <FaIcons.FaSpinner className="fa-spin me-2" />
              Guardando...
            </>
          ) : (
            <>
              <FaIcons.FaCheck className="me-2" />
              Confirmar revisión
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MarcarRevisadoModal;
